import * as THREE from 'three';
import { ColoredObjectSchema } from './ColoredObject';
import { GlintEffect } from './glint';
import { SelectableObject } from './selectableObject';
import { Vec3Schema, stringSchema } from '@/data/common';
import z from 'zod';

export const BoxSchema = ColoredObjectSchema.extend({
  name: stringSchema.default('Box'),
  size: Vec3Schema.default(new THREE.Vector3(1, 1, 1)).meta({ hidden: true }),
});

export class Box extends SelectableObject<typeof BoxSchema> {
  static override schema = BoxSchema;

  private readonly mesh: THREE.Mesh;
  private readonly material: THREE.MeshStandardMaterial;
  private readonly glintEffect: GlintEffect;

  constructor(options: z.input<typeof BoxSchema> = {}) {
    super(options);

    this.material = new THREE.MeshStandardMaterial({
      color: this.data.color,
      opacity: 0.5,
      emissive: 0x000000,
      roughness: 0.35,
      metalness: 0.05,
    });

    this.mesh = new THREE.Mesh(
      new THREE.BoxGeometry(
        this.data.size.x,
        this.data.size.y,
        this.data.size.z,
      ),
      this.material,
    );

    this.glintEffect = new GlintEffect(this.material);
  }

  protected renderObject(parent: THREE.Object3D) {
    parent.add(this.mesh);
  }

  override getPickTargets() {
    return [this.mesh];
  }

  override getAabb() {
    return new THREE.Box3().setFromCenterAndSize(
      new THREE.Vector3(0, 0, 0),
      this.data.size,
    );
  }

  override update(delta: number): void {
    this.glintEffect.update(delta);
  }

  override dispose() {
    super.dispose();
    this.glintEffect.dispose();
    this.mesh.geometry.dispose();
    this.material.dispose();
  }

  protected override onSelectionChanged(selected: boolean): void {
    this.glintEffect.setSelected(selected);
  }
}
